import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer, zIndex } from '@easy-expense/ui-web-core';
import React from 'react';

import { KeyHint } from './KeyHint.component';

const NavButton: React.FC<{
  iconName: string;
  label: string;
  hint: string;
  disabled: boolean;
  onClick: () => void;
}> = ({ iconName, label, hint, disabled, onClick }) => {
  const [hover, setHover] = React.useState(false);

  return (
    <Layout.Column style={{ position: 'relative' }}>
      <Layout.PressableColumn
        px={6}
        py={6}
        radius={100}
        bg={hover && !disabled ? 'brandPrimaryXLight' : 'white'}
        align
        justify
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1 }}
      >
        <Icon
          name={iconName}
          size={18}
          color={hover && !disabled ? theme.colors.brandPrimary : theme.colors.primary}
        />
      </Layout.PressableColumn>

      {hover && !disabled ? (
        <KeyHint label={label} hint={hint} style={{ top: 40, right: 0 }} />
      ) : null}
    </Layout.Column>
  );
};

/**
 * Floating control that shows the current expense's position within the visible
 * transaction list ("12 / 95") and lets the user jump to the previous/next
 * expense. Mirrors the Up/Down arrow-key shortcuts.
 */
export const ExpenseNavControl: React.FC<{
  currentIndex: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}> = ({ currentIndex, total, onPrevious, onNext }) => {
  if (total <= 1 || currentIndex < 0) {
    return null;
  }

  return (
    <Layout.Row
      bg="white"
      radius={100}
      px={8}
      py={4}
      align
      border={[1, 'solid', 'grayLight']}
      style={{
        position: 'absolute',
        top: 24,
        right: 24,
        zIndex: zIndex.CloseButton,
      }}
    >
      <Spacer.Horizontal size={4} />
      <OpenSans.Secondary size="xs-12" weight="bold-700">
        {currentIndex + 1} / {total}
      </OpenSans.Secondary>
      <Spacer.Horizontal size={8} />
      <NavButton
        iconName="chevron-up"
        label={getTranslation('Previous expense')}
        hint="↑"
        disabled={currentIndex <= 0}
        onClick={onPrevious}
      />
      <Spacer.Horizontal size={2} />
      <NavButton
        iconName="chevron-down"
        label={getTranslation('Next expense')}
        hint="↓"
        disabled={currentIndex >= total - 1}
        onClick={onNext}
      />
    </Layout.Row>
  );
};
